'use client'

import { switchPickupPointOfSale } from '@/feature/point-of-sale/services/pointOfSale.action'
import { PICKUP_POINT_OF_SALE_ID_COOKIE } from '@/feature/point-of-sale/services/pointOfSale.constants'
import {
  __closeSelectProductPickupPointOfSaleDrawerAtom,
  __isSelectProductPickupPointOfSaleDrawerOpenAtom,
  __selectProductPickupPointOfSaleDrawerAtom,
} from '@/feature/point-of-sale/services/pointOfSale.store'
import { useRefreshProductBuyBoxPriceAndStock } from '@/feature/product/hooks/useRefreshProductBuyBoxPriceAndStock'
import { APOLLO_CLIENT_SIDE_OPTIONS } from '@/lib/apollo/clients/apolloClient.client'
import { isSSR } from '@/lib/next/services/nextjs.rendering'
import { useQuery } from '@apollo/client'
import { gql } from '@gql-generated/gql'
import { SelectProductPickupPointOfSaleDrawerDocument } from '@gql-generated/graphql'
import { useReactiveGetCookie } from 'cookies-next/client'
import { useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useMemo, useState } from 'react'
import { SelectablePointOfSaleCard } from '../../selectable-point-of-sale-card/SelectablePointOfSaleCard.client'
import SelectPointOfSaleDrawer_V2 from '../_components/SelectPointsOfSaleDrawer.client'

gql(/* GraphQL */ `
  query SelectProductPickupPointOfSaleDrawerConfig {
    config {
      pointOfSale {
        isLocalStockByStoreEnabled
      }
    }
  }
`)

gql(/* GraphQL */ `
  query SelectProductPickupPointOfSaleDrawer($reference: String!, $searchPostalCode: String) {
    availablePointsOfSaleInventory(reference: $reference, searchPostalCode: $searchPostalCode) {
      id
      pointOfSale {
        isSelectable
        ...SelectablePointOfSaleCard_V2_pointOfSale
      }
      product {
        reference
        ...ProductAvailabilityItem_Availability
      }
    }
  }
`)

export function SelectProductPickupPointOfSaleDrawer(): React.ReactNode {
  const getCookie = useReactiveGetCookie()
  const currentPickupPointOfSaleId = getCookie(PICKUP_POINT_OF_SALE_ID_COOKIE)

  const { isOpen: isDrawerOpen, reference } = useAtomValue(
    __selectProductPickupPointOfSaleDrawerAtom,
  )

  const closeDrawer = useSetAtom(__closeSelectProductPickupPointOfSaleDrawerAtom)

  // -- Load points of sale with stock

  const [searchPostalCode, setSearchPostalCode] = useState<string>('')

  const {
    data,
    loading: isLoadingPointsOfSale,
    error: pointsOfSaleError,
  } = useQuery(SelectProductPickupPointOfSaleDrawerDocument, {
    ...APOLLO_CLIENT_SIDE_OPTIONS,
    // Keep `no-cache` as product is normalized by `reference` in the Apollo cache, but here
    // the same product has different stock data per point of sale.
    fetchPolicy: 'no-cache',
    skip: !isDrawerOpen || !reference || isSSR,
    variables: {
      reference: reference ?? '',
      searchPostalCode,
    },
  })

  const refreshProductBuyBoxPriceAndStock = useRefreshProductBuyBoxPriceAndStock()

  const handlePointOfSaleSelected = useCallback(
    async (pointOfSaleId: string) => {
      await switchPickupPointOfSale(pointOfSaleId, { setAsFavoriteToo: false })
      await refreshProductBuyBoxPriceAndStock()
      closeDrawer()
    },
    [closeDrawer, refreshProductBuyBoxPriceAndStock],
  )

  const mappedPointsOfSale = useMemo(() => {
    return (data?.availablePointsOfSaleInventory || []).map((productPointOfSale) => ({
      ...productPointOfSale,
      isSelected: productPointOfSale.pointOfSale.id === currentPickupPointOfSaleId,
    }))
  }, [data, currentPickupPointOfSaleId])

  return (
    <>
      <SelectPointOfSaleDrawer_V2
        isOpenAtom={__isSelectProductPickupPointOfSaleDrawerOpenAtom}
        pointsOfSale={mappedPointsOfSale}
        isLoading={isLoadingPointsOfSale}
        error={pointsOfSaleError}
        searchPostalCode={searchPostalCode}
        onSearchPostalCodeChangeAction={setSearchPostalCode}
        renderPointOfSale={(productPointOfSale) => {
          const isCurrentPointOfSale =
            productPointOfSale.pointOfSale.id === currentPickupPointOfSaleId

          return (
            <SelectablePointOfSaleCard
              pointOfSale={productPointOfSale.pointOfSale}
              displayStock
              availability={productPointOfSale.product}
              isStockLoading={isLoadingPointsOfSale}
              isSelectable={!isCurrentPointOfSale && productPointOfSale.pointOfSale.isSelectable}
              isSelected={productPointOfSale.pointOfSale.id === currentPickupPointOfSaleId}
              onPointOfSaleSelected={() =>
                handlePointOfSaleSelected(productPointOfSale.pointOfSale.id)
              }
            />
          )
        }}
      />
    </>
  )
}
